import {
	type QueryClient,
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import type { MemberListParams } from "../../../club-api/members";
import { clubApi } from "../../../club-api-client";
import type { AddMember, BulkUpdateMembers, UpdateClubMember } from "../../../types";
import { queryKeys } from "../../query-keys";

// The club detail embeds the member roster (the dashboard's approvals queue and
// stat cards read it), but its key is a *prefix* of the members key, so
// invalidating members never reaches it. Refresh both; `exact` keeps the detail
// refresh from fanning out to every other query under the club.
function invalidateClubMembers(queryClient: QueryClient, clubId: string) {
	return Promise.all([
		queryClient.invalidateQueries({ queryKey: queryKeys.clubPortal.members(clubId) }),
		queryClient.invalidateQueries({ exact: true, queryKey: queryKeys.clubPortal.detail(clubId) }),
	]);
}

export function useClubMemberCounts(clubId: string | null) {
	return useQuery({
		queryKey: [...queryKeys.clubPortal.members(clubId ?? ""), "counts"],
		queryFn: () => clubApi.members.counts(clubId!),
		enabled: !!clubId,
	});
}

export function useClubMembersList(clubId: string | null, params?: MemberListParams) {
	return useQuery({
		queryKey: [...queryKeys.clubPortal.members(clubId ?? ""), params],
		queryFn: () => clubApi.members.list(clubId!, params),
		enabled: !!clubId,
	});
}

/**
 * Fetch every member matching `filters` (paging through the server). Gated by
 * `enabled` so callers only pay the full-scan cost on demand — e.g. when a CSV
 * export runs or the broadcast dialog opens.
 */
export function useAllClubMembers(
	clubId: string | null,
	filters?: Omit<MemberListParams, "limit" | "offset">,
	enabled = true,
) {
	return useQuery({
		queryKey: [...queryKeys.clubPortal.members(clubId ?? ""), "all", filters],
		queryFn: () => clubApi.members.listAll(clubId!, filters),
		enabled: !!clubId && enabled,
	});
}

export function useSearchClubMembers(
	clubId: string | null,
	query: string,
	options?: {
		allowShortQuery?: boolean;
		filters?: Omit<MemberListParams, "q" | "limit" | "offset">;
	},
) {
	const filters = options?.filters;
	return useQuery({
		queryKey: [
			...queryKeys.clubPortal.members(clubId ?? ""),
			"search-members",
			query,
			...(filters ? [filters] : []),
		],
		queryFn: () =>
			clubApi.members.list(clubId!, {
				...filters,
				...(query ? { q: query } : {}),
				limit: 20,
			}),
		enabled: !!clubId && (query.length >= 2 || options?.allowShortQuery === true),
		placeholderData: options?.allowShortQuery ? keepPreviousData : undefined,
	});
}

export function useSearchUsersForInvite(clubId: string, query: string) {
	return useQuery({
		queryKey: [...queryKeys.clubPortal.members(clubId), "search", query],
		queryFn: () => clubApi.members.searchUsers(clubId, query),
		enabled: !!clubId && query.length >= 2,
	});
}

export function useAddMember(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: AddMember) => clubApi.members.add(clubId, data),
		onSuccess: () => invalidateClubMembers(queryClient, clubId),
	});
}

export function useUpdateMemberRole(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ userId, data }: { userId: string; data: UpdateClubMember }) =>
			clubApi.members.update(clubId, userId, data),
		onSuccess: () => invalidateClubMembers(queryClient, clubId),
	});
}

export function useBulkUpdateMembers(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: BulkUpdateMembers) => clubApi.members.bulkUpdate(clubId, data),
		onSuccess: () => invalidateClubMembers(queryClient, clubId),
	});
}

export function useBulkImportMembers(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (members: { email: string; role: string }[]) =>
			clubApi.members.bulkImport(clubId, members),
		onSuccess: () => invalidateClubMembers(queryClient, clubId),
	});
}

export function useRemoveMember(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (userId: string) => clubApi.members.remove(clubId, userId),
		onSuccess: () => invalidateClubMembers(queryClient, clubId),
	});
}
